import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import api from '../utils/api';
import EventPopup from './EventPopup';
import EventPreview from './EventPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';

const getStyles = (viewMode) => ({
    container: {
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '8px',
        boxShadow: 'none',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10,
        marginTop: '-24px',
        display: 'flex',
        flexDirection: 'column',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        marginTop: '0',
        zIndex: 10,
    },
    topScrollContainer: {
        height: '12px',
        overflowX: 'auto',
        overflowY: 'hidden',
        marginBottom: '0',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '6px',
        zIndex: 3,
        className: 'topScrollContainer',
        marginLeft: '300px',
        width: 'calc(100% - 300px)',
        position: 'relative',
        WebkitScrollbarHeight: '12px',
        WebkitScrollbarBackground: 'var(--bg-secondary)',
        WebkitScrollbarThumbBackground: 'var(--border-color)',
        WebkitScrollbarThumbBorderRadius: '6px',
        WebkitScrollbarThumbBorder: '3px solid var(--bg-secondary)',
        WebkitScrollbarTrackBackground: 'var(--bg-secondary)',
        WebkitScrollbarTrackBorderRadius: '6px',
    },
    scrollContent: {
        width: '1700px',
        height: '1px',
    },
    timeHeaderWrapper: {
        backgroundColor: 'var(--bg-primary)',
        zIndex: 2,
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        height: '70px',
        position: 'relative',
        borderTop: 'none',
    },
    teamColumnSpace: {
        width: '280px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
        borderRight: '1px solid var(--border-color)',
        height: '70px',
        position: 'relative',
        zIndex: 3,
        overflow: 'hidden',
    },
    timeHeader: {
        display: 'flex',
        height: '70px',
        backgroundColor: 'var(--bg-primary)',
        width: viewMode === 'week' ? '100%' : '2550px',
        position: 'relative',
        transform: 'translateX(0)',
        transition: 'transform 0ms linear',
        flex: 1,
        overflow: 'hidden',
    },
    timeHeaderCell: {
        width: viewMode === 'week' ? 'calc(100% / 7)' : '150px',
        flexShrink: 0,
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        backgroundColor: 'var(--bg-primary)',
        height: viewMode === 'week' ? '70px' : '70px',
        transition: 'all 0.2s ease',
        padding: '4px',
        paddingTop: '8px',
    },
    timeContentWrapper: {
        width: viewMode === 'week' ? '100%' : '70px',
        height: viewMode === 'week' ? '100%' : '70px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: '6px',
        padding: viewMode === 'week' ? '8px 12px' : '0px 12px',
    },
    timeNumber: {
        fontSize: '32px',
        fontWeight: '800',
        lineHeight: '1',
        marginBottom: '6px',
        textAlign: 'left',
        display: 'block',
        color: '#C0BFBF',
        fontFamily: 'Inter, sans-serif',
    },
    timePeriod: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        lineHeight: '1',
        textAlign: 'left',
        display: 'block',
    },
    fullWidthHeader: {
        height: '32px',
        backgroundColor: '#f5f6f8',
        borderBottom: '1px solid #e5e5e5',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        fontWeight: '500',
        fontSize: '16px',
        color: '#333',
        width: '100%',
        position: 'relative',
        zIndex: 2,
    },
    scheduleContent: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0, // Important for flex container
    },
    teamColumn: {
        width: '280px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
        borderRight: '1px solid var(--border-color)',
        zIndex: 3,
        position: 'sticky',
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
    },
    settingsContainer: {
        position: 'relative',
    },
    settingsButton: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#333',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
    },
    employeeCell: {
        padding: '0 12px',
        borderBottom: '1px solid #e0e0e0', // Fallback color
        borderBottomColor: 'var(--border-color)', // CSS variable for theme support
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: 'var(--bg-primary)',
        height: 'calc((100vh - 500px) / var(--visible-members))',
        minHeight: '80px', // Minimum height for usability
        transition: 'all 0.2s ease',
        minWidth: '0',
        position: 'relative',
    },
    avatar: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '500',
        fontSize: '18px',
        transition: 'all 0.2s ease'
    },
    employeeInfo: {
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        minWidth: '0',
        gap: '8px',
    },
    employeeDetails: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '0',
        flex: 1,
    },
    employeeName: {
        fontSize: '14px',
        fontWeight: '500',
        color: 'var(--text-primary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    employeeRole: {
        fontSize: '12px',
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    hoursInfo: {
        fontSize: '14px',
        color: '#4263eb',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    timeRow: {
        display: 'flex',
        height: 'calc((100vh - 500px) / var(--visible-members))',
        minHeight: '80px', // Minimum height for usability
        width: '100%',
    },
    timeSlot: {
        width: viewMode === 'week' ? 'calc(100% / 7)' : '150px',
        flexShrink: 0,
        borderRight: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
        backgroundColor: 'var(--bg-primary)',
        height: '100%',
        padding: viewMode === 'week' ? '8px' : '4px',
        display: viewMode === 'week' ? 'flex' : 'block',
        flexDirection: 'column',
        gap: '8px',
    },
    event: {
        position: viewMode === 'week' ? 'relative' : 'absolute',
        top: viewMode === 'week' ? 'auto' : '36px',
        left: viewMode === 'week' ? 'auto' : '8px',
        height: viewMode === 'week' ? '80%' : '68px',
        borderRadius: '8px',
        padding: viewMode === 'week' ? '8px 12px' : '6px 8px',
        fontSize: viewMode === 'week' ? '14px' : '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: viewMode === 'week' ? 'auto' : 'calc(100% - 16px)',
        boxShadow: viewMode === 'week' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
        margin: viewMode === 'week' ? '4px 0' : '0',
        zIndex: 1,
    },
    summaryBarContainer: {
        display: 'flex',
        width: '100%',
        backgroundColor: 'var(--bg-primary)',
        borderBottom: 'none',
    },
    summaryBarSpace: {
        width: '300px',
        flexShrink: 0,
        backgroundColor: 'var(--bg-primary)',
    },
    summaryBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '16px 24px',
        backgroundColor: 'var(--bg-primary)',
        flex: 1,
        gap: '4px',
        borderBottom: 'none',
    },
    summaryItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    summaryLabel: {
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginBottom: '4px',
    },
    summaryValue: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--text-primary)',
    },
    summaryValueWithProgress: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    summaryProgress: {
        width: '100%',
        height: '8px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    progressBar: {
        height: '100%',
        backgroundColor: '#4263eb',
        borderRadius: '2px',
        transition: 'width 0.3s ease',
    },
    weekDayName: {
        fontSize: '14px',
        fontWeight: '500',
    },
    weekDayDate: {
        fontSize: '36px',
        fontWeight: '600',
        lineHeight: '0.9',
    },
    dayColumn: {
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border-color)',
    },
    settingsPanel: {
        position: 'absolute',
        bottom: '100%',
        left: '12px',
        right: '12px',
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden',
    },
    settingsItem: {
        padding: '12px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        color: 'var(--text-primary)',
    },
    monthText: {
        fontSize: '32px',
        fontWeight: '600',
        color: 'var(--text-primary)',
    },
    yearText: {
        fontSize: '32px',
        color: 'var(--text-primary)',
        fontWeight: '600',
        marginLeft: '8px',
    },
    scheduleWrapper: {
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
    },
    scheduleSection: {
        width: '100%',
        height: '100%',
        overflow: 'auto',
        position: 'relative',
    },
    scheduleGrid: {
        width: viewMode === 'week' ? '100%' : '2550px',
        height: '100%',
        position: 'relative',
        transform: 'translateX(0)',
        transition: 'transform 0ms linear',
    },
    expandButton: {
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'absolute',
        top: '8px',
        right: '8px',
        color: 'rgba(255, 255, 255, 0.4)',
        padding: '0',
        flexShrink: 0,
    },
    expandedInfo: {
        position: 'absolute',
        left: '12px',
        right: '12px',
        top: '100%',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        padding: '12px',
        border: '1px solid var(--border-color)',
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginTop: '4px',
    },
    expandedInfoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
        fontSize: '13px',
        color: 'var(--text-primary)',
    },
    expandedInfoLabel: {
        color: 'var(--text-secondary)',
        marginRight: '12px',
    },
    expandedInfoValue: {
        color: 'var(--text-primary)',
        fontWeight: '500',
    },
});

const CustomCalendar = ({ employees, schedules, setSchedules, onScheduleCreate, viewMode, refreshSchedules, currentDate }) => {
    const styles = getStyles(viewMode);
    const hours = Array.from({ length: 17 }, (_, i) => i + 7); // 7 AM to 11 PM
    const calendarDate = currentDate || moment(); // Use passed currentDate or fallback to current date
    const currentHour = calendarDate.hour();

    // State declarations - moved here to be available to all functions
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isEventPopupOpen, setIsEventPopupOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [dragEmployee, setDragEmployee] = useState(null);
    const [dragDate, setDragDate] = useState(null);
    const [highlightedSlots, setHighlightedSlots] = useState(new Set());
    const [justFinishedDrag, setJustFinishedDrag] = useState(false);
    const [actuallyDragged, setActuallyDragged] = useState(false);
    const [existingEvent, setExistingEvent] = useState(null);
    const [prefilledParticipants, setPrefilledParticipants] = useState([]);
    const [prefilledEmployeeId, setPrefilledEmployeeId] = useState(null);
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [editingRole, setEditingRole] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [selectedColor, setSelectedColor] = useState('#4CAF50');
    const [teamMemberLimit, setTeamMemberLimit] = useState(10); // Initialize with a reasonable default
    const [windowDimensions, setWindowDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const [expandedDays, setExpandedDays] = useState(new Set());
    const MAX_VISIBLE_EVENTS = 2; // Maximum events to show before "show more" button
    
    // EventPreview state
    const [eventPreview, setEventPreview] = useState(null);
    const [eventPreviewPosition, setEventPreviewPosition] = useState({ x: 0, y: 0 });

    // Event dragging state
    const [draggingEvent, setDraggingEvent] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragPreview, setDragPreview] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    // Add ref to measure actual cell height
    const cellRef = useRef(null);
    const [actualCellHeight, setActualCellHeight] = useState(80);

    // Ref to store dragging event for immediate access
    const draggingEventRef = useRef(null);
    const dropTargetRef = useRef(null);

    // Function to get employee role (check localStorage first, then fallback to original)
    const getEmployeeRole = useCallback((employee) => {
        const savedRoles = JSON.parse(localStorage.getItem('employeeRoles') || '{}');
        return savedRoles[employee.id] || employee.role || 'Employee';
    }, []);

    // Memoize the grouped employees to prevent unnecessary recalculations
    const groupedEmployees = useMemo(() => {
        const grouped = employees.reduce((acc, employee) => {
            const role = getEmployeeRole(employee);
            if (!acc[role]) {
                acc[role] = [];
            }
            acc[role].push(employee);
            return acc;
        }, {});

        // Convert to array format and limit total employees
        const groupedArray = Object.entries(grouped).map(([role, employees]) => ({
            role,
            employees
        }));

        // Flatten all employees and limit to teamMemberLimit
        const allEmployees = groupedArray.flatMap(group => group.employees);
        const limitedEmployees = allEmployees.slice(0, teamMemberLimit);

        // Rebuild groups with limited employees
        const limitedGrouped = limitedEmployees.reduce((acc, employee) => {
            const role = getEmployeeRole(employee);
            if (!acc[role]) {
                acc[role] = [];
            }
            acc[role].push(employee);
            return acc;
        }, {});

        return Object.entries(limitedGrouped).map(([role, employees]) => ({
            role,
            employees
        }));
    }, [employees, getEmployeeRole, teamMemberLimit]);

    // Debug effect to understand the off-by-one issue
    React.useEffect(() => {
        const actualCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
        console.log('🔍 Team Member Debug:', {
            teamMemberLimit,
            actualDisplayedCount: actualCount,
            totalAvailableEmployees: employees.length,
            difference: teamMemberLimit - actualCount
        });
    }, [teamMemberLimit, groupedEmployees, employees.length]);

    // Add styles for role groups
    const roleGroupStyles = {
        roleHeader: {
            padding: '8px 16px',
            backgroundColor: 'transparent', // Will be set dynamically with role color
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '24px',
        },
        roleCount: {
            fontSize: '11px',
            color: 'var(--text-secondary)',
            fontWeight: '400',
            backgroundColor: 'var(--bg-primary)',
            padding: '2px 6px',
            borderRadius: '10px',
        },
        roleSeparator: {
            height: '4px',
            backgroundColor: 'var(--bg-primary)',
        }
    };

    // Helper function to convert hex color to rgba with transparency
    const hexToRgba = useCallback((hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }, []);

    // Debug: Log schedules changes - optimized to only log when needed
    React.useEffect(() => {
        if (schedules.length > 0) {
            console.log('📊 CustomCalendar schedules updated:', {
                totalCount: schedules.length,
                schedulesByResource: schedules.reduce((acc, schedule) => {
                    const resource = schedule.resource || 'unknown';
                    acc[resource] = (acc[resource] || 0) + 1;
                    return acc;
                }, {}),
                recentSchedules: schedules.slice(-5).map(s => ({
                    id: s.id,
                    title: s.title,
                    resource: s.resource,
                    start: moment(s.start).format('YYYY-MM-DD HH:mm')
                }))
            });
        }
    }, [schedules.length]); // Only log when count changes

    // Predefined role colors
    const roleColors = [
        '#4CAF50', // Green
        '#2196F3', // Blue
        '#FF9800', // Orange
        '#9C27B0', // Purple
        '#E91E63', // Pink
        '#00BCD4', // Cyan
        '#FF5722', // Red
        '#607D8B', // Blue Gray
        '#795548', // Brown
        '#FFC107', // Amber
    ];

    // Function to get role color
    const getRoleColor = useCallback((role) => {
        // You can store role colors in localStorage or state
        const savedColors = JSON.parse(localStorage.getItem('roleColors') || '{}');
        return savedColors[role] || '#4CAF50';
    }, []);

    // Function to save role color
    const saveRoleColor = useCallback((role, color) => {
        const savedColors = JSON.parse(localStorage.getItem('roleColors') || '{}');
        savedColors[role] = color;
        localStorage.setItem('roleColors', JSON.stringify(savedColors));
    }, []);

    // Function to update employee role
    const updateEmployeeRole = async (employeeId, newRole, color) => {
        try {
            // Since the API endpoint doesn't exist, we'll store role changes locally
            // and update the employees prop if possible
            
            // Save role color
            saveRoleColor(newRole, color);
            
            // Store employee role changes in localStorage
            const savedRoles = JSON.parse(localStorage.getItem('employeeRoles') || '{}');
            savedRoles[employeeId] = newRole;
            localStorage.setItem('employeeRoles', JSON.stringify(savedRoles));
            
            console.log('Role updated locally:', { employeeId, newRole, color });
            
            // Reset editing state
            setEditingRole(null);
            setNewRole('');
            
            // Force a re-render by updating the component state
            // This will cause the grouping to update
            setExpandedEmployees(new Set(expandedEmployees));
            
            // Optionally refresh the page to see changes
            window.location.reload();
            
        } catch (error) {
            console.error('Error updating employee role:', error);
        }
    };

    // Add styles for role editing
    const roleEditStyles = {
        roleEditContainer: {
            marginTop: '12px',
            padding: '12px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)',
        },
        roleInput: {
            width: '100%',
            padding: '8px',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-primary)',
            fontSize: '13px',
            marginBottom: '8px',
        },
        colorPicker: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
            marginBottom: '8px',
        },
        colorOption: {
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            cursor: 'pointer',
            border: '2px solid transparent',
            transition: 'all 0.2s ease',
        },
        colorOptionSelected: {
            border: '2px solid white',
            transform: 'scale(1.1)',
        },
        roleEditButtons: {
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
        },
        roleEditButton: {
            padding: '6px 12px',
            borderRadius: '4px',
            border: 'none',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        saveButton: {
            backgroundColor: '#4CAF50',
            color: 'white',
        },
        cancelButton: {
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
        },
        editRoleButton: {
            padding: '4px 8px',
            backgroundColor: 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginLeft: '8px',
        },
    };

    const formatHour = (hour) => {
        if (hour === 0) return '12';
        if (hour === 12) return '12';
        return hour > 12 ? `${hour - 12}` : `${hour}`;
    };

    // Helper function to get minute position within an hour slot
    const getMinuteFromPosition = (event, timeSlotElement) => {
        const rect = timeSlotElement.getBoundingClientRect();
        const relativeY = event.clientY - rect.top;
        const slotHeight = rect.height;
        const minuteRatio = relativeY / slotHeight;
        
        console.log('Position calculation:', { relativeY, slotHeight, minuteRatio });
        
        // If we're in the bottom 15% of the slot, consider it as the next hour
        if (minuteRatio >= 0.85) {
            console.log('Bottom of slot detected (ratio >= 0.85), setting to 60 minutes');
            return 60;
        }
        
        // Calculate minutes within this slot, snap to 15-minute intervals
        const totalMinutes = Math.round(minuteRatio * 60 / 15) * 15;
        const result = Math.max(0, Math.min(60, totalMinutes));
        
        console.log('Final minute result:', result);
        return result;
    };

    // Helper function to create time slots with highlighting
    const getTimeSlotKey = (employeeId, hour, date) => {
        const dateStr = moment(date).format('YYYY-MM-DD');
        return `${employeeId}-${hour}-${dateStr}`;
    };

    // Check if a time slot should be highlighted
    const isSlotHighlighted = (employeeId, hour, date) => {
        if (!isDragging || !dragStart || !dragEnd || dragEmployee?.id !== employeeId) {
            return false;
        }
        
        const slotKey = getTimeSlotKey(employeeId, hour, date);
        return highlightedSlots.has(slotKey);
    };

    // Calculate highlighted slots based on drag selection
    const calculateHighlightedSlots = (startHour, startMinute, endHour, endMinute, employeeId, date) => {
        const slots = [];
        const dateStr = moment(date).format('YYYY-MM-DD');
        
        for (let hour = startHour; hour <= endHour; hour++) {
            slots.push(getTimeSlotKey(employeeId, hour, date));
        }
        
        return slots;
    };

    // Handle mouse down on time slot
    const handleTimeSlotMouseDown = (event, employee, date, hour) => {
        event.preventDefault();
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        console.log('🖱️ Mouse down - drag start:', { hour, minute, employee: employee.email });
        
        setIsDragging(true);
        setActuallyDragged(false); // Reset drag flag
        setDragStart({ hour, minute });
        setDragEnd({ hour, minute }); // Start with same position, will be updated on move
        setDragEmployee(employee);
        setDragDate(date);
        
        // Start with just the starting slot highlighted
        const slots = [getTimeSlotKey(employee.id, hour, date)];
        setHighlightedSlots(new Set(slots));
        console.log('🖱️ Initial highlighted slots:', slots);
    };

    // Handle mouse move during drag
    const handleTimeSlotMouseMove = (event, employee, date, hour) => {
        if (!isDragging || !dragStart || dragEmployee?.id !== employee.id) {
            return;
        }
        
        // Prevent cross-day dragging - only allow dragging within the same date
        const dragDateStr = moment(dragDate).format('YYYY-MM-DD');
        const currentDateStr = moment(date).format('YYYY-MM-DD');
        if (dragDateStr !== currentDateStr) {
            console.log('Preventing cross-day drag:', { dragDate: dragDateStr, currentDate: currentDateStr });
            return;
        }
        
        event.preventDefault();
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        console.log('🖱️ Mouse move:', { hour, minute, dragStart, employee: employee.email });
        
        // Calculate end time based on current mouse position
        let endHour = hour;
        let endMinute = minute;
        
        // If minute is 60, we're at the bottom of the slot - move to next hour
        if (minute >= 60) {
            endHour = hour + 1;
            endMinute = 0;
            console.log('🖱️ Bottom of slot detected - moving to next hour:', { endHour, endMinute });
        }
        
        // Calculate total minutes from start
        const startTotalMinutes = dragStart.hour * 60 + dragStart.minute;
        const currentTotalMinutes = endHour * 60 + endMinute;
        const durationMinutes = currentTotalMinutes - startTotalMinutes;
        
        console.log('🖱️ Duration calculation:', { 
            startTotalMinutes, 
            currentTotalMinutes, 
            durationMinutes
        });
        
        // Only apply minimum 30-minute selection if duration is 0 or negative
        if (durationMinutes <= 0) {
            // Same position or backwards - default to 30 minutes
            const minEndTotalMinutes = startTotalMinutes + 30;
            endHour = Math.floor(minEndTotalMinutes / 60);
            endMinute = minEndTotalMinutes % 60;
            console.log('🖱️ Applied minimum 30min - final:', { endHour, endMinute });
        } else {
            // Forward drag with positive duration - use actual position
            console.log('🖱️ Using actual position - duration:', durationMinutes, 'minutes');
        }
        
        setDragEnd({ hour: endHour, minute: endMinute });
        
        // Mark that we actually dragged (moved to a different position)
        if (dragStart.hour !== endHour || Math.abs(dragStart.minute - endMinute) > 15) {
            setActuallyDragged(true);
            console.log('🖱️ Actual drag movement detected');
        }
        
        // Update highlighted slots to span from start to end
        const slots = calculateHighlightedSlots(dragStart.hour, dragStart.minute, endHour, endMinute, employee.id, date);
        setHighlightedSlots(new Set(slots));
        console.log('🖱️ Updated highlighted slots:', slots, 'total slots:', slots.length);
    };

    // Handle mouse up to complete selection
    const handleTimeSlotMouseUp = (event) => {
        if (!isDragging || !dragStart || !dragEnd || !dragEmployee || !dragDate) {
            return;
        }
        
        event.preventDefault();
        
        console.log('Drag end:', { dragStart, dragEnd, highlightedSlots });
        
        // Only open EventPopup if we actually dragged (have highlighted slots or actual movement)
        if (highlightedSlots.size > 0 && actuallyDragged) {
            console.log('Using highlighted slots for time calculation');
            
            // Extract hours from highlighted slots
            const hours = Array.from(highlightedSlots).map(slot => {
                const parts = slot.split('-');
                return parseInt(parts[1]); // hour is the second part
            }).sort((a, b) => a - b);
            
            const startHour = Math.min(...hours);
            const endHour = Math.max(...hours) + 1; // Add 1 to get the end of the last hour
            
            console.log('Highlighted slot hours:', { startHour, endHour, totalHours: endHour - startHour });
            
            const finalStartTime = moment(dragDate)
                .hour(startHour)
                .minute(0)
                .second(0);
            const finalEndTime = moment(dragDate)
                .hour(endHour)
                .minute(0)
                .second(0);
                
            console.log('Final times from highlighting:', { 
                start: finalStartTime.format('HH:mm'), 
                end: finalEndTime.format('HH:mm'),
                duration: finalEndTime.diff(finalStartTime, 'minutes') + ' minutes'
            });
            
            console.log('🎯 Setting EventPopup times:', {
                selectedEmployee: dragEmployee,
                selectedStartTime: finalStartTime.toDate(),
                selectedEndTime: finalEndTime.toDate(),
                selectedDate: dragDate,
                startTimeFormatted: finalStartTime.format('YYYY-MM-DD HH:mm:ss'),
                endTimeFormatted: finalEndTime.format('YYYY-MM-DD HH:mm:ss'),
                durationMinutes: finalEndTime.diff(finalStartTime, 'minutes')
            });
            
            // Set selected times and open popup
            setSelectedEmployee(dragEmployee);
            setSelectedStartTime(finalStartTime.format('HH:mm'));
            setSelectedEndTime(finalEndTime.format('HH:mm'));
            setSelectedDate(dragDate);
            setIsEventPopupOpen(true);
        } else {
            console.log('🖱️ No actual drag movement - letting click handler show EventPreview');
        }
        
        // Reset drag state
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setDragEmployee(null);
        setDragDate(null);
        setHighlightedSlots(new Set());
        
        // Only set flag to prevent click handler if we actually dragged
        if (actuallyDragged) {
            setJustFinishedDrag(true);
            setTimeout(() => setJustFinishedDrag(false), 100);
            console.log('🖱️ Blocking clicks after actual drag');
        } else {
            console.log('🖱️ No drag movement - allowing click handler');
        }
        setActuallyDragged(false);
    };

    // Add global mouse up listener to handle drag completion
    React.useEffect(() => {
        const handleGlobalMouseUp = (event) => {
            if (isDragging) {
                handleTimeSlotMouseUp(event);
            }
        };

        if (isDragging) {
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('mouseleave', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mouseleave', handleGlobalMouseUp);
        };
    }, [isDragging, dragStart, dragEnd, dragEmployee, dragDate]);

    // Original click handler for simple clicks (fallback)
    const handleTimeSlotClick = (event, employee, date, hour) => {
        // Prevent click if we just finished dragging
        if (justFinishedDrag) {
            console.log('🖱️ Ignoring click - just finished drag');
            return;
        }

        console.log('🎯 Time slot clicked - params:', { 
            employeeName: employee?.name, 
            employeeId: employee?.id,
            employee: employee,
            date, 
            hour
        });

        // Calculate the minute position within the hour based on click position
        const timeSlotElement = event.currentTarget;
        const minute = getMinuteFromPosition(event, timeSlotElement);
        
        // Snap to 30-minute intervals (0 or 30)
        const snappedMinute = minute < 30 ? 0 : 30;
        
        console.log('🎯 Time slot clicked:', { 
            employee: employee?.name, 
            date, 
            hour, 
            clickedMinute: minute,
            snappedMinute: snappedMinute
        });
        
        // Guard against undefined employee
        if (!employee || !employee.id) {
            console.error('❌ Employee is undefined or missing ID:', employee);
            return;
        }
        
        // Clear existing event state for new events
        setExistingEvent(null);
        setSelectedEmployee(employee);
        setSelectedDate(date);
        
        // Create start and end times based on the clicked position
        const startHour = hour;
        const startMinute = snappedMinute;
        const endMinute = snappedMinute + 30;
        
        let endHour = startHour;
        let finalEndMinute = endMinute;
        
        // If end minute goes beyond 60, move to next hour
        if (endMinute >= 60) {
            endHour = startHour + 1;
            finalEndMinute = endMinute - 60;
        }
        
        const startTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
        const endTime = `${endHour.toString().padStart(2, '0')}:${finalEndMinute.toString().padStart(2, '0')}`;
        
        setSelectedStartTime(startTime);
        setSelectedEndTime(endTime);
        
        console.log('🎯 Setting EventPreview for new event:', {
            startTime,
            endTime,
            employee: employee.name,
            date: date,
            clickPosition: `${hour}:${snappedMinute}`
        });
        
        // Create a new event object for preview
        const dateString = moment(date).format('YYYY-MM-DD');
        const startMoment = moment(`${dateString} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${dateString} ${endTime}`, 'YYYY-MM-DD HH:mm');
        
        // Validate the moments are valid
        if (!startMoment.isValid() || !endMoment.isValid()) {
            console.error('Invalid date/time created:', { date, startTime, endTime, dateString });
            return;
        }
        
        const newEvent = {
            title: '',
            start: startMoment.toDate(),
            end: endMoment.toDate(),
            resource: employee.id,
            isNew: true,
            employee: employee
        };
        
        // Set the event preview position based on click position
        const rect = timeSlotElement.getBoundingClientRect();
        setEventPreviewPosition({
            x: rect.left + rect.width / 2,
            y: rect.top
        });
        
        setEventPreview(newEvent);
    };

    // Handler for existing event clicks
    const handleEventClick = (event, scheduleEvent) => {
        event.stopPropagation();
        
        console.log('📅 Existing event clicked:', scheduleEvent);
        
        // Set the event preview position based on click position
        const rect = event.currentTarget.getBoundingClientRect();
        setEventPreviewPosition({
            x: rect.left + rect.width / 2,
            y: rect.top
        });
        
        setEventPreview(scheduleEvent);
    };

    // Handler for event preview expand (opens full EventPopup)
    const handleEventPreviewExpand = (eventData) => {
        console.log('🔄 Expanding EventPreview to EventPopup:', {
            isNew: eventData.isNew,
            employee: eventData.employee,
            hasZoomInfo: !!(eventData.zoomInfo || eventData.zoom_info),
            hasParticipants: !!(eventData.participants && eventData.participants.length > 0),
            hasUploadedFiles: !!(eventData.uploadedFiles && eventData.uploadedFiles.length > 0),
            startDate: eventData.start,
            endDate: eventData.end
        });
        
        setEventPreview(null);
        
        if (eventData.isNew) {
            // For new events, create a pseudo-existing event with all the data from preview
            // This allows EventPopup to access zoom info, files, etc.
            const pseudoExistingEvent = {
                ...eventData,
                // Ensure we have the necessary fields
                title: eventData.title || '',
                description: eventData.description || '',
                location: eventData.location || '',
                participants: eventData.participants || [],
                files: eventData.uploadedFiles || [],
                zoom_info: eventData.zoomInfo || null,
                isNew: true // Keep the flag to indicate it's still a new event
            };
            
            console.log('🎯 Created pseudo-existing event for new event:', pseudoExistingEvent);
            
            setExistingEvent(pseudoExistingEvent);
            setSelectedEmployee(eventData.employee);
            
            // Fix date handling to avoid timezone issues
            if (eventData.start instanceof Date) {
                // Create a new Date to avoid mutation and ensure proper date handling
                const dateForPopup = new Date(eventData.start.getFullYear(), eventData.start.getMonth(), eventData.start.getDate());
                setSelectedDate(dateForPopup);
                setSelectedStartTime(moment(eventData.start).format('HH:mm'));
                setSelectedEndTime(moment(eventData.end).format('HH:mm'));
            } else {
                console.warn('eventData.start is not a Date object:', eventData.start);
                setSelectedDate(new Date());
                setSelectedStartTime('09:00');
                setSelectedEndTime('10:00');
            }
        } else {
            // For existing events, set up for editing
            setExistingEvent(eventData);
            setSelectedEmployee(employees.find(emp => emp.id === parseInt(eventData.resource)));
            
            // Fix date handling for existing events too
            if (eventData.start instanceof Date) {
                const dateForPopup = new Date(eventData.start.getFullYear(), eventData.start.getMonth(), eventData.start.getDate());
                setSelectedDate(dateForPopup);
                setSelectedStartTime(moment(eventData.start).format('HH:mm'));
                setSelectedEndTime(moment(eventData.end).format('HH:mm'));
            } else {
                console.warn('Existing event start is not a Date object:', eventData.start);
                setSelectedDate(new Date(moment(eventData.start).format('YYYY-MM-DD')));
                setSelectedStartTime(moment(eventData.start).format('HH:mm'));
                setSelectedEndTime(moment(eventData.end).format('HH:mm'));
            }
        }
        
        setIsEventPopupOpen(true);
    };

    // Handler for event preview close
    const handleEventPreviewClose = () => {
        setEventPreview(null);
    };

    // Add resize handler
    React.useEffect(() => {
        let resizeTimeout;
        
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Use more efficient selectors and cache DOM elements
                const scheduleGrid = document.querySelector('.scheduleGrid');
                const timeHeader = document.querySelector('.timeHeader');

                if (scheduleGrid && timeHeader) {
                    const newWidth = viewMode === 'week' ? '100%' : '2550px'; // Updated width
                    scheduleGrid.style.width = newWidth;
                    timeHeader.style.width = newWidth;
                }

                // Only update heights if elements exist and are visible
                const teamColumn = document.querySelector('.teamColumn');
                const scheduleSection = document.querySelector('.scheduleSection');
                
                if (teamColumn && scheduleSection && teamColumn.offsetParent !== null) {
                    teamColumn.style.height = `${scheduleSection.scrollHeight}px`;
                }
            }, 150); // Debounce by 150ms for DOM operations
        };

        handleResize(); // Call once immediately
        window.addEventListener('resize', handleResize, { passive: true });
        
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [viewMode]);

    // Listen for AI chat calendar refresh events
    useEffect(() => {
        const handleCalendarRefresh = async (event) => {
            console.log('🔄 Calendar refresh event received:', event.detail);
            if (refreshSchedules) {
                console.log('🔄 Triggering calendar refresh from AI event...');
                
                // Try multiple refresh attempts with delays
                try {
                    await refreshSchedules();
                    console.log('✅ First refresh completed');
                    
                    // Add a second refresh after a short delay
                    setTimeout(async () => {
                        console.log('🔄 Second refresh attempt...');
                        try {
                            await refreshSchedules();
                            console.log('✅ Second refresh completed');
                        } catch (error) {
                            console.error('❌ Second refresh failed:', error);
                        }
                    }, 1000);
                    
                } catch (error) {
                    console.error('❌ Calendar refresh failed:', error);
                }
            } else {
                console.warn('⚠️ refreshSchedules function not available');
            }
        };

        // Add event listener for custom calendar refresh events
        window.addEventListener('refreshCalendar', handleCalendarRefresh);
        
        // Cleanup
        return () => {
            window.removeEventListener('refreshCalendar', handleCalendarRefresh);
        };
    }, [refreshSchedules]);

    // Get the start and end dates for the week view
    const getWeekRange = () => {
        const startOfWeek = calendarDate.clone().startOf('week');
        const endOfWeek = calendarDate.clone().endOf('week');
        return { startOfWeek, endOfWeek };
    };

    // Calculate hours between two dates
    const calculateHours = (start, end) => {
        const startMoment = moment(start);
        const endMoment = moment(end);
        const duration = moment.duration(endMoment.diff(startMoment));
        return Math.round(duration.asHours() * 100) / 100; // Round to 2 decimal places
    };

    // Memoize the expensive schedule filtering function
    const getSchedulesForEmployeeAndTime = useCallback((employeeId, hour, date = null) => {
        const filteredSchedules = schedules.filter(schedule => {
            const scheduleStart = moment(schedule.start);
            const scheduleEnd = moment(schedule.end);
            
            // Use the provided date or current date for day view
            const targetDate = date ? moment(date) : calendarDate;
            
            // Only show events in their starting hour to avoid duplicates
            const scheduleStartHour = scheduleStart.hour();
            const isStartingHour = scheduleStartHour === hour;

            // For day view, check if the schedule is for the target date
            if (viewMode === 'day') {
                const isTargetDate = scheduleStart.isSame(targetDate, 'day');
                return schedule.resource === String(employeeId) && 
                       isTargetDate &&
                       isStartingHour;
            }
            
            // For week view, check if the schedule is within the current week
            const { startOfWeek, endOfWeek } = getWeekRange();
            const isThisWeek = scheduleStart.isBetween(startOfWeek, endOfWeek, 'day', '[]');
            
            return schedule.resource === String(employeeId) && 
                   isThisWeek &&
                   isStartingHour;
        });
        
        return filteredSchedules.map(schedule => {
            const scheduleStart = moment(schedule.start);
            const scheduleEnd = moment(schedule.end);
            
            // Calculate how many hours this event spans
            const startHour = scheduleStart.hour();
            const endHour = scheduleEnd.hour();
            const startMinute = scheduleStart.minute();
            const endMinute = scheduleEnd.minute();
            
            // Calculate total duration in hours
            const totalHours = endHour - startHour + (endMinute > 0 ? 1 : 0);
            
            return {
                ...schedule,
                startMinute,
                endMinute,
                totalHours,
                startHour,
                endHour,
                isMultiHour: totalHours > 1
            };
        });
    }, [schedules, viewMode, calendarDate]);

    const saveScheduleToServer = async (schedule) => {
        try {
            // Format the schedule data for the server
            const serverSchedule = {
                title: schedule.title || 'Work Hours',
                shift_date: moment(schedule.start).format('YYYY-MM-DD'),
                start_time: moment(schedule.start).format('HH:mm:ss'),
                end_time: moment(schedule.end).format('HH:mm:ss'),
                calendar_type: schedule.calendarId || 'shift',
                is_all_day: schedule.isAllDay || false,
                user_id: schedule.resource?.id,
                company_id: schedule.resource?.company_id,
                description: schedule.description || '',
                location: schedule.location || '',
                color: schedule.backgroundColor || schedule.color || '#4263eb',
                type: 'work'
            };

            console.log('Sending schedule data:', serverSchedule);

            // Use the API utility which handles token validation
            const response = await api.post('/api/schedules', serverSchedule);
            
            console.log('Schedule saved successfully:', response.data);

            // Then automatically record hours for the new schedule
            if (response.data.calendar_type === 'shift' || response.data.type === 'work') {
                try {
                    const eventForHours = {
                        ...response.data,
                        start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                        end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                        user_id: response.data.user_id,
                        company_id: response.data.company_id
                    };

                    console.log('Recording hours for new schedule:', eventForHours);
                    
                    await recordHours(eventForHours);
                    console.log('Hours automatically recorded for new schedule');
                } catch (hourError) {
                    console.error('Failed to record hours for new schedule:', hourError);
                    // Don't throw the error - we still want to return the saved schedule
                }
            } else {
                console.log('Skipping hours recording - not a work/shift event:', {
                    calendar_type: response.data.calendar_type,
                    type: response.data.type
                });
            }

            return response.data;
        } catch (error) {
            console.error('Error saving schedule:', {
                error: error.message,
                response: error.response?.data,
                schedule: schedule
            });
            throw error;
        }
    };

    const updateScheduleOnServer = async (schedule) => {
        try {
            // Format the schedule data for the server
            const serverSchedule = {
                title: schedule.title || 'Work Hours',
                shift_date: moment(schedule.start).format('YYYY-MM-DD'),
                start_time: moment(schedule.start).format('HH:mm:ss'),
                end_time: moment(schedule.end).format('HH:mm:ss'),
                calendar_type: schedule.calendarId || 'shift',
                is_all_day: schedule.isAllDay || false,
                user_id: parseInt(schedule.resource),
                company_id: schedule.company_id,
                description: schedule.description || '',
                location: schedule.location || '',
                color: schedule.backgroundColor || schedule.color || '#4263eb',
                type: 'work'
            };

            console.log('Updating schedule data:', serverSchedule);

            // Use the API utility which handles token validation
            const response = await api.put(`/api/schedules/${schedule.id}`, serverSchedule);
            
            console.log('Schedule updated successfully:', response.data);

            return response.data;
        } catch (error) {
            console.error('Error updating schedule:', {
                error: error.message,
                response: error.response?.data,
                schedule: schedule
            });
            throw error;
        }
    };

    const recordHours = async (event) => {
        try {
            const hours = calculateHours(event.start, event.end);
            const startTime = moment(event.start);
            const endTime = moment(event.end);

            console.log('Recording hours for event:', {
                eventId: event.id,
                title: event.title,
                start: startTime.format('YYYY-MM-DD HH:mm:ss'),
                end: endTime.format('YYYY-MM-DD HH:mm:ss'),
                calculatedHours: hours
            });

            const requestData = {
                user_id: event.user_id,
                event_id: event.id,
                work_date: startTime.format('YYYY-MM-DD'),
                hours_worked: hours,
                company_id: event.company_id,
                start_time: startTime.format('HH:mm:ss'),
                end_time: endTime.format('HH:mm:ss'),
                source: 'calendar'
            };

            console.log('Sending hours recording request:', requestData);

            // Use the API utility which handles token validation
            const recordResponse = await api.post('/api/schedules/hours', requestData);
            console.log('Hours recorded successfully:', recordResponse.data);

            // Verify the hours were recorded
            const verifyResponse = await api.get('/api/hours/summary', {
                params: {
                    user_id: event.user_id,
                    company_id: event.company_id,
                    start_date: startTime.format('YYYY-MM-DD'),
                    end_date: endTime.format('YYYY-MM-DD')
                }
            });
            console.log('Hours verification response:', verifyResponse.data);

            // Trigger a refresh of the hours summary
            if (window.refreshHoursSummary) {
                console.log('Triggering hours summary refresh after recording');
                window.refreshHoursSummary();
            } else {
                console.warn('refreshHoursSummary function not found');
            }

            return recordResponse.data;
        } catch (error) {
            console.error('Error recording hours:', {
                error: error.message,
                response: error.response?.data,
                event: {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end
                }
            });
            throw error;
        }
    };

    const calculateMonthlyHours = (employeeId) => {
        const currentMonth = moment().format('YYYY-MM');
        return schedules
            .filter(schedule => {
                const scheduleMonth = moment(schedule.start).format('YYYY-MM');
                return schedule.resource === String(employeeId) && scheduleMonth === currentMonth;
            })
            .reduce((total, schedule) => {
                // Only count hours for:
                // 1. Regular work schedules (no countsTowardHours property)
                // 2. PTO requests (countsTowardHours === true)
                // Skip regular time-off requests (countsTowardHours === false)
                if (schedule.hasOwnProperty('countsTowardHours') && !schedule.countsTowardHours) {
                    return total; // Skip this event - it's regular time-off, not PTO
                }
                
                const hours = calculateHours(schedule.start, schedule.end);
                return total + hours;
            }, 0);
    };

    const calculateTotalHours = () => {
        const currentMonth = moment().format('YYYY-MM');
        return Math.round(schedules.reduce((total, schedule) => {
            if (moment(schedule.start).format('YYYY-MM') === currentMonth) {
                return total + calculateHours(schedule.start, schedule.end);
            }
            return total;
        }, 0));
    };

    const totalHours = calculateTotalHours();
    const totalTarget = employees.length * 200; // 200 hours per employee

    const getProgressBarColor = (percentage) => {
        if (percentage < 70) return '#348944'; // green
        if (percentage < 90) return '#FFAE00'; // yellow
        return '#EC1817'; // red
    };

    const weeklyProgressWidth = Math.min((totalHours / 40) * 100, 100);
    const monthlyProgressWidth = Math.min((totalHours / totalTarget) * 100, 100);

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const getDarkerColor = (color) => {
        try {
            if (!color) return '#163c8d';
            
            // Remove any leading # if present
            const hex = color.replace('#', '');
            
            // Ensure we have a valid 6-character hex
            if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#163c8d';
            
            // Convert to RGB
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            
            // Make it darker by reducing each component by 60% (more subtle)
            const darkerR = Math.max(Math.floor(r * 0.4), 1);
            const darkerG = Math.max(Math.floor(g * 0.4), 1);
            const darkerB = Math.max(Math.floor(b * 0.4), 1);
            
            // Convert back to hex
            return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
        } catch (error) {
            console.error('Error creating darker color:', error);
            return '#163c8d';
        }
    };

    const getLighterColor = (color) => {
        try {
            if (!color) return '#e3f2fd';
            
            let r, g, b;
            
            // Handle rgb/rgba format
            if (color.startsWith('rgb')) {
                const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (rgbMatch) {
                    r = parseInt(rgbMatch[1]);
                    g = parseInt(rgbMatch[2]);
                    b = parseInt(rgbMatch[3]);
                } else {
                    return '#e3f2fd';
                }
            } else {
                // Handle hex format
                const hex = color.replace('#', '');
                
                // Ensure we have a valid 6-character hex
                if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#e3f2fd';
                
                // Convert to RGB
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            }
            
            // Mix with white (255, 255, 255) to create a lighter version
            // Using 85% white + 15% original color for a very light tint
            const mixRatio = 0.15;
            const lighterR = Math.round(r * mixRatio + 255 * (1 - mixRatio));
            const lighterG = Math.round(g * mixRatio + 255 * (1 - mixRatio));
            const lighterB = Math.round(b * mixRatio + 255 * (1 - mixRatio));
            
            // Convert back to hex
            return `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
        } catch (error) {
            console.error('Error creating lighter color:', error);
            return '#e3f2fd';
        }
    };

    const renderAvatar = (employee) => {
        const initial = employee.first_name?.charAt(0)?.toUpperCase() || 
                       employee.email?.charAt(0)?.toUpperCase();
        const backgroundColor = getRandomPastelColor(employee.email);
        
        return (
            <div style={{
                ...styles.avatar,
                backgroundColor,
                color: 'white'
            }}>
                {initial}
            </div>
        );
    };

    // Add handler for prefilling participants
    const handlePrefillParticipant = (employee, event) => {
        event.stopPropagation(); // Prevent any parent click handlers
        console.log('🎯 Prefilling participant:', employee);
        
        // Check if employee is already selected
        const isAlreadySelected = prefilledParticipants.some(p => p.id === employee.id);
        
        if (isAlreadySelected) {
            // If already selected, remove them
            setPrefilledParticipants(prev => prev.filter(p => p.id !== employee.id));
            setPrefilledEmployeeId(null); // Clear the single employee ID since we're using multiple
        } else {
            // If not selected, add them to the array
            setPrefilledParticipants(prev => [...prev, employee]);
            setPrefilledEmployeeId(null); // Clear the single employee ID since we're using multiple
        }
        
        // Provide visual feedback that the participant has been selected
        const button = event.currentTarget;
        const originalBg = button.style.backgroundColor;
        const originalBorder = button.style.borderColor;
        
        // Flash green for adding, red for removing
        button.style.backgroundColor = isAlreadySelected ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)';
        button.style.borderColor = isAlreadySelected ? 'rgba(239, 68, 68, 0.5)' : 'rgba(34, 197, 94, 0.5)';
        
        setTimeout(() => {
            button.style.backgroundColor = originalBg;
            button.style.borderColor = originalBorder;
        }, 200);
        
        console.log('🎯 Updated prefilled participants:', isAlreadySelected ? 'removed' : 'added', employee.email);
    };

    const handleEventSave = async (eventData) => {
        try {
            console.log('💾 Saving event data:', {
                title: eventData.title,
                start: eventData.start,
                end: eventData.end,
                filesCount: eventData.files ? eventData.files.length : 0,
            });

            // Get the employee based on the selected resource
            const employee = employees.find(emp => emp.id === parseInt(eventData.resource));
            if (!employee) {
                throw new Error('Employee not found for the selected resource');
            }

            // Get user_id and company_id from the employee
            const user_id = employee.user_id;
            const company_id = employee.company_id;

            console.log('👤 Employee details:', { user_id, company_id, email: employee.email });

            // Format dates for server - handle both Date objects and time strings
            let startDate, endDate;
            
            if (eventData.start instanceof Date && eventData.end instanceof Date) {
                // If we have Date objects, use them directly
                startDate = eventData.start;
                endDate = eventData.end;
                console.log('📅 Using Date objects directly');
            } else if (typeof eventData.start === 'string' && typeof eventData.end === 'string') {
                // If we have time strings, combine with selectedDate
                console.log('📅 Processing time strings with selectedDate:', {
                    selectedDate: selectedDate,
                    selectedDateType: typeof selectedDate,
                    selectedDateIsDate: selectedDate instanceof Date,
                    eventStart: eventData.start,
                    eventEnd: eventData.end
                });
                
                const baseDate = selectedDate ? (selectedDate instanceof Date ? selectedDate : new Date(selectedDate)) : new Date();
                
                // Validate baseDate is valid
                if (isNaN(baseDate.getTime())) {
                    console.error('❌ Invalid baseDate created:', { selectedDate, baseDate });
                    throw new Error(`Invalid base date: ${selectedDate}`);
                }
                
                // Parse time strings like "09:30" or "10:00"
                const [startHour, startMinute] = eventData.start.split(':').map(Number);
                const [endHour, endMinute] = eventData.end.split(':').map(Number);
                
                startDate = new Date(baseDate);
                startDate.setHours(startHour, startMinute, 0, 0);
                
                endDate = new Date(baseDate);
                endDate.setHours(endHour, endMinute, 0, 0);
                
                console.log('📅 Combined date and time strings:', {
                    baseDate: baseDate.toISOString(),
                    startTime: eventData.start,
                    endTime: eventData.end,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                });
            } else {
                throw new Error('Invalid date format in event data');
            }

            // Validate the dates
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                throw new Error('Invalid date/time values');
            }

            // Create schedule data object
            const scheduleData = {
                title: eventData.title,
                shift_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0],
                start_time: startDate.toLocaleTimeString('en-US', { hour12: false }),
                end_time: endDate.toLocaleTimeString('en-US', { hour12: false }),
                is_all_day: eventData.isAllday || false,
                calendar_type: eventData.calendarId || 'default',
                user_id: user_id,
                company_id: company_id,
                resource: eventData.resource,
                color: eventData.backgroundColor || eventData.color || '#4b8dff',
                recurrence_rule: eventData.recurrenceRule || null,
                zoom_info: eventData.zoom_info || null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                participants: eventData.participants ? eventData.participants.map(p => p.email).filter(Boolean) : []
            };

            let response;

            // Check if this is an update to an existing event
            if (existingEvent && existingEvent.id) {
                console.log('🔄 Updating existing event:', existingEvent.id);
                
                if (eventData.files && eventData.files.length > 0) {
                    // Handle file uploads for updates...
                    // [Previous file upload code remains unchanged]
                } else {
                    // Update without files
                    response = await api.put(`/api/schedules/${existingEvent.id}`, scheduleData);
                    console.log('✅ Schedule updated:', response.data);
                }
            } else {
                console.log('➕ Creating new event');
                
                // Check if we have files to upload
                const hasFiles = eventData.files && eventData.files.length > 0;
                
                if (hasFiles) {
                    console.log('📎 Files detected, using FormData for upload');
                    
                    // Create FormData object for file uploads
                    const formData = new FormData();
                    
                    // Add all schedule data to FormData
                    Object.entries(scheduleData).forEach(([key, value]) => {
                        if (value !== null && value !== undefined) {
                            if (Array.isArray(value)) {
                                formData.append(key, JSON.stringify(value));
                            } else if (typeof value === 'object') {
                                formData.append(key, JSON.stringify(value));
                            } else {
                                formData.append(key, value);
                            }
                        }
                    });
                    
                    // Add files to FormData
                    eventData.files.forEach((file) => {
                        formData.append('files', file.file || file);
                    });
                    
                    // Add file metadata
                    formData.append('fileMetadata', JSON.stringify(eventData.files.map(file => ({
                        originalName: file.name,
                        type: file.type,
                        size: file.size,
                        lastModified: file.lastModified
                    }))));
                    
                    // Use fetch instead of axios for FormData
                    const token = localStorage.getItem('token');
                    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
                    
                    const fetchResponse = await fetch(`${API_BASE_URL}/api/schedules`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                        body: formData
                    });
                    
                    if (!fetchResponse.ok) {
                        const responseText = await fetchResponse.text();
                        console.error('Server response:', fetchResponse.status, responseText);
                        throw new Error(`Failed to save schedule: ${responseText}`);
                    }
                    
                    const savedSchedule = await fetchResponse.json();
                    console.log('✅ Schedule with files created:', savedSchedule);
                    response = { id: savedSchedule.id };
                    
                } else {
                    // No files, use regular JSON API call
                    console.log('📄 No files, using JSON API call');
                    
                    // Create all schedules (main + participants) directly via API
                    const allSchedulesToCreate = [];
                    
                    // Add the main schedule
                    allSchedulesToCreate.push({
                        scheduleData: scheduleData,
                        employee: employee,
                        isMain: true
                    });
                    
                    // Add participant schedules
                    if (eventData.participants && eventData.participants.length > 0) {
                        console.log('🔄 Preparing participant events...');
                        
                        for (const participant of eventData.participants) {
                            // Skip if participant is the same as the main resource
                            if (participant.id === parseInt(eventData.resource)) {
                                console.log(`⏭️ Skipping participant ${participant.email} - same as main resource`);
                                continue;
                            }

                            // Create participant schedule data
                            const participantScheduleData = {
                                ...scheduleData,
                                user_id: participant.user_id,
                                resource: String(participant.id)
                            };

                            allSchedulesToCreate.push({
                                scheduleData: participantScheduleData,
                                employee: participant,
                                isMain: false
                            });
                        }
                    }
                    
                    console.log(`🔄 Creating ${allSchedulesToCreate.length} total schedules (1 main + ${allSchedulesToCreate.length - 1} participants)`);
                    
                    // Create all schedules
                    for (const { scheduleData: data, employee: emp, isMain } of allSchedulesToCreate) {
                        try {
                            console.log(`🔄 Creating ${isMain ? 'main' : 'participant'} event for: ${emp.email}`);
                            const apiResponse = await api.post('/api/schedules', data);
                            console.log(`✅ ${isMain ? 'Main' : 'Participant'} schedule created for ${emp.email}:`, apiResponse.data);

                            if (isMain) {
                                response = { id: apiResponse.data.id };
                            }
                        } catch (error) {
                            console.error(`❌ Failed to create schedule for ${isMain ? 'main' : 'participant'} ${emp.email}:`, error);
                            if (isMain) {
                                throw error; // If main event fails, throw error
                            }
                        }
                    }
                }

                // After all schedules are created, refresh the schedules from the server
                console.log('🔄 Refreshing schedules after creation...');
                await refreshSchedules();
            }

            return response;
        } catch (error) {
            console.error('Error saving schedule:', error);
            throw error;
        }
    };

    const handleEventDelete = async (eventId) => {
        try {
            console.log('🗑️ Deleting event with ID:', eventId);
            
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await api.delete(`/api/schedules/${eventId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200) {
                console.log('✅ Event deleted successfully');
                // Close the popup first
                setIsEventPopupOpen(false);
                // Refresh schedules from server instead of managing state locally
                console.log('🔄 Refreshing schedules after deletion...');
                await refreshSchedules();
            } else {
                throw new Error(`Failed to delete event: ${response.status}`);
            }
        } catch (error) {
            console.error('❌ Error deleting event:', error);
            throw error;
        }
    };

    const handleExistingEventClick = useCallback(async (schedule, employee, date) => {
        try {
            // First, set the basic event data
            setExistingEvent(schedule);
            setSelectedEmployee(employee);
            setSelectedDate(date);
            
            // Set the start and end times from the schedule
            const startTime = moment(schedule.start).format('HH:mm');
            const endTime = moment(schedule.end).format('HH:mm');
            
            setSelectedStartTime(startTime);
            setSelectedEndTime(endTime);

            // Check if files are missing and fetch them if needed
            if (!schedule.files || schedule.files.length === 0) {
                console.log('Files missing from schedule data, fetching from server...');
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
                        const response = await fetch(`${API_BASE_URL}/api/schedules/${schedule.id}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        
                        if (response.ok) {
                            const fullEventData = await response.json();
                            console.log('Fetched full event data:', fullEventData);
                            if (fullEventData.files && fullEventData.files.length > 0) {
                                // Update the schedule with files
                                const updatedSchedule = { ...schedule, files: fullEventData.files };
                                setExistingEvent(updatedSchedule);
                                console.log('Added files to schedule:', fullEventData.files);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching event files:', error);
                }
            }
            
            setIsEventPopupOpen(true);
        } catch (error) {
            console.error('Error handling existing event click:', error);
            // Still open the popup even if file fetching fails
            setIsEventPopupOpen(true);
        }
    }, []);

    // Helper functions to check for Zoom meetings and participant files
    const hasZoomMeeting = useCallback((schedule) => {
        return schedule.zoom_info && (
            schedule.zoom_info.join_url || 
            schedule.zoom_info.joinUrl ||
            schedule.zoom_info.meeting_id ||
            schedule.zoom_info.id
        );
    }, []);

    const hasParticipantFiles = useCallback((schedule) => {
        return schedule.files && Array.isArray(schedule.files) && schedule.files.length > 0;
    }, []);

    const renderEvent = useCallback((schedule, employee, date, hour) => {
        const eventColor = schedule.color || schedule.backgroundColor || '#4b8dff';
        
        // Convert hex to RGB for background color
        const getRGBBackground = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgb(${Math.floor(r * 0.2)}, ${Math.floor(g * 0.2)}, ${Math.floor(b * 0.2)})`;
        };
        
        const bgColor = eventColor.startsWith('#') ? getRGBBackground(eventColor) : eventColor;
        
        // Calculate position and size based on minutes within the hour
        const startMinute = schedule.startMinute || 0;
        const endMinute = schedule.endMinute || 60;
        const totalHours = schedule.totalHours || 1;
        const isMultiHour = schedule.isMultiHour || false;
        
        // Format time display
        const formatTime = (date) => {
            if (!date) return '';
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const displayMinutes = minutes.toString().padStart(2, '0');
            return `${displayHours}:${displayMinutes}${period}`;
        };
        
        const timeDisplay = schedule.start && schedule.end 
            ? `${formatTime(schedule.start)} - ${formatTime(schedule.end)}`
            : '';
        
        // Common styles for both views
        const eventStyles = {
            background: bgColor,
            backgroundColor: bgColor,
            color: eventColor,
            border: `2px solid ${eventColor}`,
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            overflow: 'hidden',
            zIndex: 1,
            boxShadow: `inset 0 0 0 1000px ${bgColor}`, // Force background fill
            WebkitBoxShadow: `inset 0 0 0 1000px ${bgColor}`, // For Safari
            MozBoxShadow: `inset 0 0 0 1000px ${bgColor}`, // For Firefox
        };
        
        if (viewMode === 'week') {
            // Week view - simpler positioning
            return (
                <div
                    key={schedule.id}
                    style={{
                        ...eventStyles,
                        position: 'relative',
                        height: '60px',
                        width: '100%',
                        marginBottom: '4px',
                        cursor: draggingEvent?.id === schedule.id ? 'grabbing' : 'grab',
                    }}
                    draggable={true}
                    onMouseDown={(event) => {
                        // Check if it's a left click
                        if (event.button === 0) {
                            handleEventDragStart(event, schedule);
                        }
                    }}
                    onClick={(event) => {
                        // Only handle click if we're not dragging
                        if (!draggingEvent) {
                            handleEventClick(event, schedule);
                        }
                    }}
                    onDragStart={(e) => e.preventDefault()} // Prevent default HTML5 drag
                >
                    <div style={{
                        padding: '6px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 1,
                        fontFamily: 'Inter, sans-serif',
                        height: '100%',
                        color: eventColor,
                        background: bgColor,
                        boxShadow: `inset 0 0 0 1000px ${bgColor}`,
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            width: '100%',
                        }}>
                            <div style={{
                                flex: 1,
                                paddingRight: '4px',
                            }}>
                                <div style={{
                                    fontSize: '14px',  // Increased from 12px
                                    fontWeight: '600',
                                    lineHeight: '1.3', // Slightly increased line height
                                    marginBottom: timeDisplay ? '4px' : '0', // Increased spacing
                                    color: eventColor,
                                    background: bgColor,
                                }}>
                                    {schedule.title || 'Work Hours'}
                                </div>
                                {timeDisplay && (
                                    <div style={{
                                        fontSize: '12px',  // Increased from 10px
                                        fontWeight: '500', // Made slightly bolder
                                        opacity: 0.9,      // Made more visible
                                        lineHeight: '1.2', // Slightly increased line height
                                        color: eventColor,
                                        background: bgColor,
                                    }}>
                                        {timeDisplay}
                                    </div>
                                )}
                            </div>
                            {/* Event Icons */}
                            {(hasZoomMeeting(schedule) || hasParticipantFiles(schedule) || (schedule.participants && schedule.participants.length > 0)) && (
                                <div style={{
                                    display: 'flex',
                                    gap: '2px',
                                    marginTop: '2px',
                                }}>
                                    {hasZoomMeeting(schedule) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="6.5" viewBox="0 0 20 13" fill="rgba(255, 255, 255, 0.9)" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                                            </svg>
                                        </div>
                                    )}
                                    {hasParticipantFiles(schedule) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"/>
                                            </svg>
                                        </div>
                                    )}
                                    {(schedule.participants && schedule.participants.length > 0) && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            background: eventColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        
        // Day view - calculate actual pixel values using state dimensions
        const cellPadding = 4;
        
        // Calculate actual number of displayed employees (including role headers)
        const displayedEmployeeCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
        const roleHeaderCount = groupedEmployees.length;
        const totalDisplayRows = displayedEmployeeCount + roleHeaderCount;
        
        // Calculate actual cell height: use measured height from DOM
        const usableHeight = Math.max(actualCellHeight - 25, 25); // Even more conservative padding for better fit
        
        // Calculate top position based on start minute
        let topPosition = cellPadding + (startMinute / 60) * usableHeight;
        
        // In day view, adjust 30-minute events to be positioned slightly lower
        if (viewMode === 'day' && startMinute === 30) {
            topPosition += 16; // Push 30-minute events down by 16px
        }
        
        // Calculate event height based on duration
        const duration = endMinute - startMinute;
        let eventHeight = (duration / 60) * usableHeight;
        
        // Make events thicker by applying a responsive thickness multiplier
        // Use even less thickness when there are more team members to ensure proper fit
        const thicknessMultiplier = totalDisplayRows > 5 ? 1.0 : 1.2;
        eventHeight = eventHeight * thicknessMultiplier;
        
        // Ensure minimum height for visibility
        const minHeight = totalDisplayRows > 5 ? 15 : 20;
        eventHeight = Math.max(eventHeight, minHeight);
        
        // For multi-hour events, calculate width to span multiple cells
        let eventWidth = `calc(100% - ${cellPadding * 2}px)`;
        if (isMultiHour) {
            const cellWidth = 150; // Width of each hour cell
            eventWidth = `${totalHours * cellWidth - (cellPadding * 2)}px`;
        }
        
        return (
            <div
                key={`${schedule.id}-${teamMemberLimit}-${totalDisplayRows}`}
                style={{
                    ...eventStyles,
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: `${cellPadding}px`,
                    height: `${eventHeight}px`,
                    width: eventWidth,
                    cursor: draggingEvent?.id === schedule.id ? 'grabbing' : 'grab',
                }}
                draggable={true}
                onMouseDown={(event) => {
                    // Check if it's a left click
                    if (event.button === 0) {
                        handleEventDragStart(event, schedule);
                    }
                }}
                onClick={(event) => {
                    // Only handle click if we're not dragging
                    if (!draggingEvent) {
                        handleEventClick(event, schedule);
                    }
                }}
                onDragStart={(e) => e.preventDefault()} // Prevent default HTML5 drag
            >
                <div style={{
                    padding: '4px 6px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: eventHeight > 30 ? 'flex-start' : 'center',
                    position: 'relative',
                    zIndex: 1,
                    fontFamily: 'Inter, sans-serif',
                    height: '100%',
                    color: eventColor,
                    background: bgColor,
                    boxShadow: `inset 0 0 0 1000px ${bgColor}`,
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                    }}>
                        <div style={{
                            flex: 1,
                            paddingRight: '4px',
                        }}>
                            <div style={{
                                fontSize: '13px',  // Increased from 11px
                                fontWeight: '600',
                                lineHeight: '1.3', // Slightly increased line height
                                marginBottom: timeDisplay && eventHeight > 30 ? '3px' : '0', // Increased spacing
                                color: eventColor,
                                background: bgColor,
                            }}>
                                {schedule.title || 'Work Hours'}
                            </div>
                            {timeDisplay && eventHeight > 30 && (
                                <div style={{
                                    fontSize: '11px',  // Increased from 9px
                                    fontWeight: '500', // Made slightly bolder
                                    opacity: 0.9,      // Made more visible
                                    lineHeight: '1.2', // Slightly increased line height
                                    color: eventColor,
                                    background: bgColor,
                                }}>
                                    {timeDisplay}
                                </div>
                            )}
                        </div>
                        {/* Event Icons */}
                        {(hasZoomMeeting(schedule) || hasParticipantFiles(schedule) || (schedule.participants && schedule.participants.length > 0)) && (
                            <div style={{
                                display: 'flex',
                                gap: '2px',
                                marginTop: '2px',
                            }}>
                                {hasZoomMeeting(schedule) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="6.5" viewBox="0 0 20 13" fill="rgba(255, 255, 255, 0.9)" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                                        </svg>
                                    </div>
                                )}
                                {hasParticipantFiles(schedule) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"/>
                                        </svg>
                                    </div>
                                )}
                                {(schedule.participants && schedule.participants.length > 0) && (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        background: eventColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                    }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
                                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }, [viewMode, windowDimensions, teamMemberLimit, groupedEmployees, actualCellHeight, handleEventClick]);

    // Add function to toggle expanded state
    const toggleEmployeeExpanded = (employeeId, event) => {
        event.stopPropagation(); // Prevent any parent click handlers
        setExpandedEmployees(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    // Update the expanded info panel
    const renderExpandedInfo = (employee) => {
        const isEditingThisRole = editingRole === employee.id;
        const currentRole = getEmployeeRole(employee); // Use the new function
        const roleColor = getRoleColor(currentRole);

        return (
            <div style={styles.expandedInfo}>
                <div style={styles.expandedInfoRow}>
                    <span style={styles.expandedInfoLabel}>Email:</span>
                    <span style={styles.expandedInfoValue}>{employee.email}</span>
                                    </div>
                <div style={styles.expandedInfoRow}>
                    <span style={styles.expandedInfoLabel}>Role:</span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: roleColor,
                                marginRight: '8px',
                            }}></div>
                            <span style={styles.expandedInfoValue}>{currentRole}</span>
                                    </div>
                        <button
                            style={roleEditStyles.editRoleButton}
                            onClick={() => {
                                setEditingRole(employee.id);
                                setNewRole(currentRole);
                                setSelectedColor(roleColor);
                            }}
                            title="Edit role"
                        >
                            Edit
                        </button>
                                </div>
                            </div>

                {/* Role editing interface */}
                {isEditingThisRole && (
                    <div style={roleEditStyles.roleEditContainer}>
                        <input
                            type="text"
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            placeholder="Enter role name"
                            style={roleEditStyles.roleInput}
                            autoFocus
                        />
                        
                        <div style={roleEditStyles.colorPicker}>
                            {roleColors.map(color => (
                                <div
                                    key={color}
                                    style={{
                                        ...roleEditStyles.colorOption,
                                        backgroundColor: color,
                                        ...(selectedColor === color ? roleEditStyles.colorOptionSelected : {})
                                    }}
                                    onClick={() => setSelectedColor(color)}
                                    title={`Select ${color}`}
                                />
                            ))}
                </div>

                        <div style={roleEditStyles.roleEditButtons}>
                            <button
                                            style={{
                                    ...roleEditStyles.roleEditButton,
                                    ...roleEditStyles.cancelButton
                                }}
                                onClick={() => {
                                    setEditingRole(null);
                                    setNewRole('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...roleEditStyles.roleEditButton,
                                    ...roleEditStyles.saveButton
                                }}
                                onClick={() => updateEmployeeRole(employee.id, newRole, selectedColor)}
                                disabled={!newRole.trim()}
                            >
                                Save
                            </button>
                                        </div>
                                </div>
                        )}
                                    </div>
                                );
    };

    // Function to handle role-wide participant selection
    const handleRoleParticipantToggle = (role, employees) => {
        const roleEmployeeIds = employees.map(emp => emp.id);
        const allRoleSelected = roleEmployeeIds.every(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        
        if (allRoleSelected) {
            // Remove all employees from this role
            setPrefilledParticipants(prev => 
                prev.filter(p => !roleEmployeeIds.includes(p.id))
            );
        } else {
            // Add all employees from this role
            const newParticipants = employees.filter(emp => 
                !prefilledParticipants.some(p => p.id === emp.id)
            );
            setPrefilledParticipants(prev => [...prev, ...newParticipants]);
        }
    };

    // Update the role header to show role colors
    const renderRoleHeader = (role, count, employees) => {
        const roleColor = getRoleColor(role);
        const roleEmployeeIds = employees.map(emp => emp.id);
        const allRoleSelected = roleEmployeeIds.every(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        const someRoleSelected = roleEmployeeIds.some(id => 
            prefilledParticipants.some(p => p.id === id)
        );
        
        return (
                                    <div style={{
                ...roleGroupStyles.roleHeader,
                backgroundColor: hexToRgba(roleColor, 0.05),
                color: hexToRgba(roleColor, 0.8)
                                    }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                                            style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: allRoleSelected ? '#666666' : (someRoleSelected ? '#999999' : 'transparent'),
                            border: `2px solid #666666`,
                            borderRadius: '3px',
                            marginRight: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0'
                        }}
                        onClick={() => handleRoleParticipantToggle(role, employees)}
                        title={allRoleSelected ? `Remove all ${role}s from participants` : `Add all ${role}s as participants`}
                    >
                        {allRoleSelected && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                        {someRoleSelected && !allRoleSelected && (
                            <div style={{
                                width: '6px',
                                height: '2px',
                                backgroundColor: 'white',
                                borderRadius: '1px'
                            }}></div>
                        )}
                    </button>
                    <span>{role}</span>
                    </div>
                <span style={{
                    ...roleGroupStyles.roleCount,
                    color: hexToRgba(roleColor, 0.6)
                }}>
                    {count}
                </span>
                </div>
        );
    };

    // Update the team column rendering to use the new functions
    const renderTeamColumn = () => {
        return (
                    <div style={styles.teamColumn} className="teamColumn">
                {groupedEmployees.map((group, groupIndex) => (
                    <React.Fragment key={group.role}>
                        {/* Role header with color */}
                        {renderRoleHeader(group.role, group.employees.length, group.employees)}
                        
                        {/* Employees in this role */}
                        {group.employees.map(employee => {
                            const monthlyHours = Math.round(calculateMonthlyHours(employee.id) * 100) / 100;
                            const isExpanded = expandedEmployees.has(employee.id);
                            
                            return (
                                <div 
                                    key={employee.id} 
                                    style={styles.employeeCell} 
                                    className="employeeCell"
                                    ref={groupIndex === 0 && group.employees.indexOf(employee) === 0 ? cellRef : null}
                                >
                                    {/* Participant selection box */}
                                    <div 
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            backgroundColor: prefilledParticipants.some(p => p.id === employee.id) ? '#2D2D2D' : 'transparent',
                                            border: '2px solid #2D2D2D',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            marginRight: '8px',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={(e) => handlePrefillParticipant(employee, e)}
                                        title={`${prefilledParticipants.some(p => p.id === employee.id) ? 'Remove' : 'Add'} ${employee.email.split('@')[0]} ${prefilledParticipants.some(p => p.id === employee.id) ? 'from' : 'to'} participants`}
                                    >
                                        {prefilledParticipants.some(p => p.id === employee.id) && (
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                    {renderAvatar(employee)}
                                    <div style={styles.employeeInfo}>
                                        <div style={styles.employeeDetails}>
                                            <div style={styles.employeeName}>{employee.email.split('@')[0]}</div>
                                            <div style={styles.employeeRole}>{getEmployeeRole(employee)}</div>
                                        </div>
                                        <div style={styles.hoursInfo}>
                                            {monthlyHours}/200h
                                        </div>
                                    </div>

                                    {/* Add expand button */}
                                    <button
                                        style={{
                                            ...styles.expandButton,
                                            opacity: isExpanded ? 1 : 0.6,
                                        }}
                                        onClick={(e) => toggleEmployeeExpanded(employee.id, e)}
                                        title={isExpanded ? 'Show less' : 'Show more'}
                                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                                        onMouseLeave={(e) => e.target.style.opacity = isExpanded ? '1' : '0.6'}
                                    >
                                        <svg 
                                            width="12" 
                                            height="12" 
                                            viewBox="0 0 16 16" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
                                            <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                            <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
                                        </svg>
                                    </button>

                                    {/* Expanded info panel with role editing */}
                                    {isExpanded && renderExpandedInfo(employee)}
                                </div>
                            );
                        })}
                        
                        {/* Add separator between role groups (except for the last group) */}
                        {groupIndex < groupedEmployees.length - 1 && (
                            <div style={roleGroupStyles.roleSeparator}></div>
                        )}
                    </React.Fragment>
                ))}
                    </div>
        );
    };

    // Update the schedule grid rendering to match the grouped structure
    const renderScheduleGrid = () => {
        return (
                            <div style={styles.scheduleGrid} className="scheduleGrid">
                {groupedEmployees.map((group, groupIndex) => (
                    <React.Fragment key={`schedule-${group.role}`}>
                        {/* Role header divider - separate from grid */}
                        <div style={{
                            width: '100%',
                            height: '24px',
                            backgroundColor: hexToRgba(getRoleColor(group.role), 0.08),
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: hexToRgba(getRoleColor(group.role), 0.8),
                            position: 'relative',
                            zIndex: 2,
                        }}>
                            {/* This is a true divider, not part of the time grid */}
                        </div>
                        
                        {/* Employee rows for this role group */}
                        {group.employees.map((employee, employeeIndex) => (
                                    <div key={employee.id} style={styles.timeRow} className="timeRow">
                                        {viewMode === 'week' ? (
                                            Array.from({ length: 7 }, (_, dayIndex) => {
                                                const date = calendarDate.clone().startOf('week').add(dayIndex, 'days');
                                                const daySchedules = schedules.filter(schedule => {
                                                    const scheduleStart = moment(schedule.start);
                                                    return schedule.resource === String(employee.id) && 
                                                           scheduleStart.isSame(date, 'day');
                                                });

                                                const isHighlighted = isSlotHighlighted(employee.id, 0, date.toDate());
                                                const isExpanded = isDayExpanded(employee.id, date.toDate());
                                                const visibleSchedules = isExpanded ? daySchedules : daySchedules.slice(0, MAX_VISIBLE_EVENTS);
                                                const hasMoreEvents = daySchedules.length > MAX_VISIBLE_EVENTS;
                                                const isLastInGroup = employeeIndex === group.employees.length - 1;
                                                const dropTargetStyles = getDropTargetStyles(employee.id, 0, date.toDate());

                                                return (
                                                    <div key={dayIndex} style={{
                                                        ...styles.timeSlot,
                                                        ...(isHighlighted && {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                                            boxShadow: 'inset 0 0 0 2px #ef4444',
                                                            boxSizing: 'border-box',
                                                            borderRadius: '2px',
                                                            margin: '1px',
                                                        }),
                                                        cursor: isDragging ? 'grabbing' : 'pointer',
                                                        transition: 'all 0.1s ease',
                                                        ...dropTargetStyles
                                                    }}>
                                                        {/* Add grid overlay */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                                                            pointerEvents: 'none'
                                                        }} />
                                                        
                                                        {/* Render visible events */}
                                                        {visibleSchedules.map(schedule => 
                                                            renderEvent(schedule, employee, date.toDate(), moment(schedule.start).hour())
                                                        )}
                                                        
                                                        {/* Show more button */}
                                                        {hasMoreEvents && !isExpanded && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '4px',
                                                                    right: '4px',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                    color: 'white',
                                                                    borderRadius: '12px',
                                                                    padding: '2px 8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    zIndex: 10,
                                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDayExpansion(employee.id, date.toDate());
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                                                    e.target.style.transform = 'scale(1)';
                                                                }}
                                                            >
                                                                +{daySchedules.length - MAX_VISIBLE_EVENTS}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Show less button when expanded */}
                                                        {hasMoreEvents && isExpanded && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '4px',
                                                                    right: '4px',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                    color: 'white',
                                                                    borderRadius: '12px',
                                                                    padding: '2px 8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    zIndex: 10,
                                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDayExpansion(employee.id, date.toDate());
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                                                    e.target.style.transform = 'scale(1)';
                                                                }}
                                                            >
                                                                Show Less
                                                            </div>
                                                        )}
                                                        
                                                        <div
                                                            style={{
                                                                cursor: isDragging ? 'grabbing' : 'pointer',
                                                                backgroundColor: 'transparent',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                zIndex: 0,
                                                                border: 'none'
                                                            }}
                                                            data-employee-id={employee.id}
                                                            data-hour={0}
                                                            data-date={date.format('YYYY-MM-DD')}
                                                            onMouseDown={(e) => handleTimeSlotMouseDown(e, employee, date.toDate(), 0)}
                                                            onMouseMove={(e) => handleTimeSlotMouseMove(e, employee, date.toDate(), 0)}
                                                            onClick={(e) => !isDragging && handleTimeSlotClick(e, employee, date.toDate(), 0)}
                                                        />
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            hours.map(hour => {
                                                const isHighlighted = isSlotHighlighted(employee.id, hour, calendarDate.toDate());
                                                const dropTargetStyles = getDropTargetStyles(employee.id, hour, calendarDate.toDate());
                                                
                                                return (
                                                    <div key={hour} style={{
                                                        ...styles.timeSlot,
                                                        ...(isHighlighted && {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                                            boxShadow: 'inset 0 0 0 2px #ef4444',
                                                            boxSizing: 'border-box',
                                                            borderRadius: '2px',
                                                            margin: '1px',
                                                        }),
                                                        cursor: isDragging ? 'grabbing' : 'pointer',
                                                        transition: 'all 0.1s ease',
                                                        ...dropTargetStyles
                                                    }}>
                                                        {getSchedulesForEmployeeAndTime(employee.id, hour, calendarDate.toDate()).map(schedule => 
                                                            renderEvent(schedule, employee, calendarDate.toDate(), hour)
                                                        )}
                                                        <div
                                                            style={{
                                                                cursor: isDragging ? 'grabbing' : 'pointer',
                                                                backgroundColor: 'transparent',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                zIndex: 0,
                                                                border: 'none',
                                                                userSelect: 'none'
                                                            }}
                                                            data-employee-id={employee.id}
                                                            data-hour={hour}
                                                            data-date={calendarDate.format('YYYY-MM-DD')}
                                                            onMouseDown={(e) => handleTimeSlotMouseDown(e, employee, calendarDate.toDate(), hour)}
                                                            onMouseMove={(e) => handleTimeSlotMouseMove(e, employee, calendarDate.toDate(), hour)}
                                                            onClick={(e) => !isDragging && handleTimeSlotClick(e, employee, calendarDate.toDate(), hour)}
                                                        />
                                                        
                                                        {/* Add visual indicators for 30-minute marks */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: 0,
                                                            right: 0,
                                                            height: '1px',
                                                            backgroundColor: 'var(--border-color)',
                                                            opacity: 0.3,
                                                            pointerEvents: 'none'
                                                        }} />
                                                        
                                                        {/* Add hour divider grid lines */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRight: '1px solid var(--border-color)',
                                                            borderBottom: '1px solid var(--border-color)',
                                                            pointerEvents: 'none',
                                                            opacity: 0.5
                                                        }} />
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                ))}
                        
                        {/* Add separator between role groups in schedule grid */}
                        {groupIndex < groupedEmployees.length - 1 && (
                            <div style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: 'var(--bg-primary)',
                            }}>
                                {/* This is also a true separator, not grid cells */}
                            </div>
                        )}
                    </React.Fragment>
                ))}
                        </div>
        );
    };

    // Team member selector component
    const renderTeamMemberSelector = () => {
        // Generate dynamic options based on actual employee count
        const totalEmployees = employees.length;
        const generateOptions = () => {
            if (totalEmployees <= 5) {
                return [1, 2, 3, 4, 5].filter(num => num <= totalEmployees);
            } else if (totalEmployees <= 10) {
                return [5, totalEmployees];
            } else if (totalEmployees <= 20) {
                return [5, 10, totalEmployees];
            } else if (totalEmployees <= 50) {
                return [5, 10, 20, totalEmployees];
            } else {
                // For larger companies, provide reasonable increments
                const options = [5, 10, 20, 50];
                // Add quarter, half, and full company size
                const quarter = Math.ceil(totalEmployees / 4);
                const half = Math.ceil(totalEmployees / 2);
                
                if (quarter > 50 && !options.includes(quarter)) options.push(quarter);
                if (half > 50 && !options.includes(half)) options.push(half);
                if (!options.includes(totalEmployees)) options.push(totalEmployees);
                
                return options.sort((a, b) => a - b);
            }
        };
        
        const options = generateOptions();
        
        return (
            <div style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                fontSize: '14px',
                color: 'var(--text-primary)'
            }}>
                <span style={{ 
                    fontSize: '13px', 
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                }}>
                    Show:
                </span>
                <select
                    value={teamMemberLimit}
                    onChange={(e) => setTeamMemberLimit(parseInt(e.target.value))}
                    style={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        outline: 'none',
                        minWidth: '60px'
                    }}
                >
                    {options.map(option => (
                        <option key={option} value={option}>
                            {option === totalEmployees ? `All (${option})` : option}
                        </option>
                    ))}
                </select>
                <span style={{ 
                    fontSize: '13px', 
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                }}>
                    of {totalEmployees} members
                </span>
            </div>
        );
    };

    // Update CSS variable for member count and handle window resize
    React.useEffect(() => {
        const updateCSSVariable = () => {
            const root = document.documentElement;
            // Calculate actual displayed employee count
            const displayedEmployeeCount = groupedEmployees.reduce((total, group) => total + group.employees.length, 0);
            root.style.setProperty('--visible-members', displayedEmployeeCount);
        };

        updateCSSVariable();

        // Debounced resize handler to prevent excessive calls
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                updateCSSVariable();
                // Update window dimensions to trigger event recalculation
                setWindowDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }, 150); // Debounce by 150ms
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [groupedEmployees]);

    // Add function to toggle day expansion
    const toggleDayExpansion = (employeeId, date) => {
        const dayKey = `${employeeId}-${moment(date).format('YYYY-MM-DD')}`;
        const newExpandedDays = new Set(expandedDays);
        
        if (newExpandedDays.has(dayKey)) {
            newExpandedDays.delete(dayKey);
        } else {
            newExpandedDays.add(dayKey);
        }
        
        setExpandedDays(newExpandedDays);
    };

    // Check if a day is expanded
    const isDayExpanded = (employeeId, date) => {
        const dayKey = `${employeeId}-${moment(date).format('YYYY-MM-DD')}`;
        return expandedDays.has(dayKey);
    };

    // Update teamMemberLimit when employees are first loaded
    React.useEffect(() => {
        if (employees.length > 0 && teamMemberLimit === 10 && employees.length !== 10) {
            // Only update if we're still at the default value and employee count is different
            setTeamMemberLimit(employees.length);
        }
    }, [employees.length, teamMemberLimit]);

    // Force re-render when teamMemberLimit changes to ensure events recalculate
    React.useEffect(() => {
        // Force window dimensions update to trigger event recalculation
        setWindowDimensions({
            width: window.innerWidth,
            height: window.innerHeight
        });
    }, [teamMemberLimit]);

    // Measure actual cell height from DOM
    React.useEffect(() => {
        const measureCellHeight = () => {
            if (cellRef.current) {
                const height = cellRef.current.getBoundingClientRect().height;
                if (height > 0 && height !== actualCellHeight) {
                    setActualCellHeight(height);
                }
            }
        };

        measureCellHeight();
        
        // Re-measure after a short delay to ensure layout is complete
        const timer = setTimeout(measureCellHeight, 100);
        
        return () => clearTimeout(timer);
    }, [teamMemberLimit, groupedEmployees, windowDimensions]);

    // Event drag handlers for moving existing events
    const handleEventDragStart = (event, scheduleEvent) => {
        event.stopPropagation();
        console.log('🎯 Starting to drag event:', scheduleEvent);
        
        const rect = event.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        });
        
        // Store in both state and ref for immediate access
        setDraggingEvent(scheduleEvent);
        draggingEventRef.current = scheduleEvent;
        
        setDragPreview({
            ...scheduleEvent,
            x: event.clientX,
            y: event.clientY
        });
        
        // Prevent event preview from showing during drag
        setEventPreview(null);
        
        // Add global mouse move and up listeners
        document.addEventListener('mousemove', handleEventDragMove);
        document.addEventListener('mouseup', handleEventDragEnd);
    };

    const dragAnimationRef = useRef(null);

    const handleEventDragMove = (event) => {
        if (!draggingEventRef.current) return;
        
        // Use requestAnimationFrame to throttle updates for smooth performance
        if (dragAnimationRef.current) {
            cancelAnimationFrame(dragAnimationRef.current);
        }
        
        dragAnimationRef.current = requestAnimationFrame(() => {
            // Update drag preview position
            setDragPreview(prev => ({
                ...prev,
                x: event.clientX,
                y: event.clientY
            }));
            
            // Find the element under the cursor (throttled)
            const elementUnderCursor = document.elementFromPoint(event.clientX, event.clientY);
            if (elementUnderCursor) {
                const timeSlot = elementUnderCursor.closest('[data-employee-id][data-hour][data-date]');
                if (timeSlot) {
                    const employeeId = parseInt(timeSlot.getAttribute('data-employee-id'));
                    const hour = parseInt(timeSlot.getAttribute('data-hour'));
                    const date = timeSlot.getAttribute('data-date');
                    
                    const newDropTarget = {
                        employeeId,
                        hour,
                        date: moment(date).toDate()
                    };
                    
                    // Only update if the drop target actually changed
                    const currentDropTarget = dropTargetRef.current;
                    const hasChanged = !currentDropTarget || 
                        currentDropTarget.employeeId !== employeeId ||
                        currentDropTarget.hour !== hour ||
                        moment(currentDropTarget.date).format('YYYY-MM-DD') !== moment(newDropTarget.date).format('YYYY-MM-DD');
                    
                    if (hasChanged) {
                        setDropTarget(newDropTarget);
                        dropTargetRef.current = newDropTarget;
                    }
                } else {
                    // Only clear if we currently have a drop target
                    if (dropTargetRef.current) {
                        setDropTarget(null);
                        dropTargetRef.current = null;
                    }
                }
            } else {
                // Only clear if we currently have a drop target
                if (dropTargetRef.current) {
                    setDropTarget(null);
                    dropTargetRef.current = null;
                }
            }
        });
    };

    const handleEventDragEnd = async (event) => {
        const currentDraggingEvent = draggingEventRef.current;
        const currentDropTarget = dropTargetRef.current;
        
        // Clean up animation frame
        if (dragAnimationRef.current) {
            cancelAnimationFrame(dragAnimationRef.current);
            dragAnimationRef.current = null;
        }
        
        // Remove global listeners
        document.removeEventListener('mousemove', handleEventDragMove);
        document.removeEventListener('mouseup', handleEventDragEnd);
        
        if (currentDraggingEvent && currentDropTarget) {
            try {
                // Handle all-day events differently
                if (currentDraggingEvent.isAllDay) {
                    // For all-day events, just change the date and employee
                    const newStart = moment(currentDropTarget.date).startOf('day').toDate();
                    const newEnd = moment(currentDropTarget.date).endOf('day').toDate();
                    
                    const updatedEvent = {
                        ...currentDraggingEvent,
                        start: newStart,
                        end: newEnd,
                        resource: String(currentDropTarget.employeeId)
                    };
                    
                    // Update the existing event
                    await updateScheduleOnServer(updatedEvent);
                    
                    // Update local schedules
                    const updatedSchedules = schedules.map(schedule => 
                        schedule.id === currentDraggingEvent.id ? updatedEvent : schedule
                    );
                    setSchedules(updatedSchedules);
                    
                } else {
                    // Handle regular timed events
                    const originalStart = moment(currentDraggingEvent.start);
                    const originalEnd = moment(currentDraggingEvent.end);
                    const duration = moment.duration(originalEnd.diff(originalStart));
                    
                    // Create new start time based on drop target
                    const newStart = moment(currentDropTarget.date)
                        .hour(currentDropTarget.hour)
                        .minute(originalStart.minute())
                        .second(0)
                        .millisecond(0);
                    
                    // Calculate new end time maintaining the same duration
                    const newEnd = moment(newStart).add(duration);
                    
                    // Update the event with new times and employee
                    const updatedEvent = {
                        ...currentDraggingEvent,
                        start: newStart.toDate(),
                        end: newEnd.toDate(),
                        resource: String(currentDropTarget.employeeId)
                    };
                    
                    // Update the existing event
                    await updateScheduleOnServer(updatedEvent);
                    
                    // Update local schedules
                    const updatedSchedules = schedules.map(schedule => 
                        schedule.id === currentDraggingEvent.id ? updatedEvent : schedule
                    );
                    setSchedules(updatedSchedules);
                }
                
                // Refresh schedules from server
                if (refreshSchedules) {
                    refreshSchedules();
                }
                
            } catch (error) {
                console.error('❌ Error moving event:', error);
            }
        }
        
        // Clear drag state
        setDraggingEvent(null);
        draggingEventRef.current = null;
        setDragPreview(null);
        setDropTarget(null);
        dropTargetRef.current = null;
        setDragOffset({ x: 0, y: 0 });
    };

    // Render drag preview
    const renderDragPreview = () => {
        if (!dragPreview) return null;

        const employee = employees.find(emp => emp.id === parseInt(dragPreview.resource));
        const eventColor = employee?.color || '#4CAF50';

        return (
            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    transform: `translate(${dragPreview.x - dragOffset.x}px, ${dragPreview.y - dragOffset.y}px)`,
                    width: '200px',
                    height: '40px',
                    background: `${eventColor}15`,
                    border: `2px solid ${eventColor}`,
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: eventColor,
                    zIndex: 9999,
                    pointerEvents: 'none',
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    willChange: 'transform',
                }}
            >
                {dragPreview.title || 'Work Hours'}
            </div>
        );
    };

    // Add drop target highlighting styles - memoized for performance
    const getDropTargetStyles = useMemo(() => {
        return (employeeId, hour, date) => {
            if (!dropTarget) return {};
            
            const targetDate = moment(date).format('YYYY-MM-DD');
            const dropTargetDate = moment(dropTarget.date).format('YYYY-MM-DD');
            
            if (dropTarget.employeeId === employeeId && 
                dropTarget.hour === hour && 
                dropTargetDate === targetDate) {
                return {
                    backgroundColor: 'rgba(66, 99, 235, 0.3)',
                    border: '2px solid #4263eb',
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s ease',
                };
            }
            
            return {};
        };
    }, [dropTarget]);

    return (
        <div style={{
            ...styles.container,
            // Add any dynamic styles here
        }}>
            <div style={styles.mainContent}>
                <div style={{ position: 'relative', height: '16px' }}>
                    <div 
                        className="topScrollContainer" 
                        style={styles.topScrollContainer} 
                        onScroll={(e) => {
                            const scheduleGrid = document.querySelector('.scheduleGrid');
                            const timeHeader = document.querySelector('.timeHeader');
                            if (scheduleGrid) {
                                scheduleGrid.style.transform = `translateX(-${e.target.scrollLeft}px)`;
                            }
                            if (timeHeader) {
                                timeHeader.style.transform = `translateX(-${e.target.scrollLeft}px)`;
                            }
                        }}
                    >
                        <div style={styles.scrollContent}></div>
                    </div>
                </div>

                <div style={styles.summaryBarContainer}>
                    <div style={styles.summaryBarSpace}></div>
                    <div style={styles.summaryBar}>
                        <div style={{...styles.summaryItem, flex: 'none', width: '300px'}}>
                            <span style={styles.summaryLabel}>Total working hours per week</span>
                            <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                <div style={{flex: 1}}>
                                    <div style={{...styles.summaryProgress}}>
                                        <div style={{
                                            ...styles.progressBar,
                                            width: `${weeklyProgressWidth}%`,
                                            backgroundColor: getProgressBarColor(weeklyProgressWidth)
                                        }} />
                                    </div>
                                </div>
                                <span style={{...styles.summaryValue, marginLeft: '8px'}}>{totalHours}h</span>
                            </div>
                        </div>
                        <div style={{width: '1px', height: '48px', backgroundColor: '#e5e5e5', margin: '8px 24px 0'}} />
                        <div style={{...styles.summaryItem, flex: 2}}>
                            <span style={styles.summaryLabel}>Total for {moment().format('MMMM')}</span>
                            <div style={{...styles.summaryValueWithProgress, flexDirection: 'row', alignItems: 'center', gap: '16px'}}>
                                <div style={{flex: 1}}>
                                    <div style={styles.summaryProgress}>
                                        <div style={{
                                            ...styles.progressBar,
                                            width: `${monthlyProgressWidth}%`,
                                            backgroundColor: getProgressBarColor(monthlyProgressWidth)
                                        }} />
                                    </div>
                                </div>
                                <span style={{...styles.summaryValue, marginLeft: '8px'}}>{totalHours}/{totalTarget}h</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.timeHeaderWrapper}>
                    <div style={styles.teamColumnSpace}></div>
                    <div style={styles.timeHeader} className="timeHeader">
                        {viewMode === 'week' ? (
                            // Week view - show days
                            Array.from({ length: 7 }, (_, i) => {
                                const date = calendarDate.clone().startOf('week').add(i, 'days');
                                return (
                                    <div key={i} style={styles.timeHeaderCell}>
                                        <div style={styles.timeContentWrapper}>
                                            <span style={{
                                                ...styles.weekDayName,
                                                color: '#C8C8C8',
                                                marginBottom: '4px',
                                                marginTop: '-12px'
                                            }} 
                                            ref={el => el && (el.style.cssText += '; color: #C8C8C8 !important')}
                                            >{date.format('ddd')}</span>
                                            <span style={{
                                                ...styles.weekDayDate,
                                                color: '#C8C8C8',
                                                marginTop: '-4px'
                                            }}
                                            ref={el => el && (el.style.cssText += '; color: #C8C8C8 !important')}
                                            >{date.format('D')}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Day view - show hours
                            hours.map(hour => (
                                <div key={hour} style={styles.timeHeaderCell}>
                                    <div style={{
                                        ...styles.timeContentWrapper,
                                        backgroundColor: 'transparent',
                                    }}>
                                        <span 
                                            className={`time-number ${hour === currentHour ? 'current-hour' : ''}`}
                                            style={{
                                                ...styles.timeNumber,
                                                color: hour === currentHour ? '#BA2C2C' : '#C0BFBF',
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: '800'
                                            }}
                                        >
                                            {formatHour(hour)}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main schedule content */}
                <div style={styles.scheduleContent}>
                    {renderTeamColumn()}
                    <div style={styles.scheduleWrapper}>
                        <div style={styles.scheduleSection} className="scheduleSection">
                            {renderScheduleGrid()}
                        </div>
                    </div>
                </div>
            </div>
            <EventPopup
                isOpen={isEventPopupOpen}
                onClose={() => {
                    setIsEventPopupOpen(false);
                    setExistingEvent(null);
                    setPrefilledParticipants([]);
                    setPrefilledEmployeeId(null);
                }}
                onSave={handleEventSave}
                onDelete={handleEventDelete}
                employee={selectedEmployee}
                startTime={selectedStartTime}
                endTime={selectedEndTime}
                date={selectedDate}
                existingEvent={existingEvent}
                prefilledParticipants={prefilledParticipants}
                employees={employees}
            />
            {eventPreview && (
                <EventPreview
                    event={eventPreview}
                    isOpen={true}
                    position={eventPreviewPosition}
                    onExpand={handleEventPreviewExpand}
                    onClose={handleEventPreviewClose}
                    employees={employees}
                    isCreating={eventPreview.isNew}
                    selectedDate={eventPreview.isNew ? new Date(eventPreview.start) : null}
                    onCreate={handleEventSave}
                />
            )}
            {renderTeamMemberSelector()}
            {renderDragPreview()}
        </div>
    );
};

export default CustomCalendar; 